import { Link } from "react-router-dom";
import {
  SignIn as SignInIcon,
  Users,
  Package,
  Lightning,
  Buildings,
  CreditCard,
  Wrench,
  Lifebuoy,
  DownloadSimple,
} from "@phosphor-icons/react";
import { useAuth } from "../context/AuthContext";
import Layout from "../components/Layout";
import MarketingLayout from "../components/MarketingLayout";

const Section = ({ icon: Icon, title, children, id }) => (
  <section id={id} className="mb-10" data-testid={`help-section-${id}`}>
    <div className="flex items-center gap-3 mb-4">
      <div className="h-9 w-9 bg-[#1A2332] text-[#FF9800] flex items-center justify-center">
        <Icon size={18} weight="duotone" />
      </div>
      <h2 className="font-display text-xl sm:text-2xl text-zinc-900">{title}</h2>
    </div>
    <div className="bg-white border border-zinc-300 p-5 sm:p-6 text-sm leading-relaxed text-zinc-700 space-y-3">
      {children}
    </div>
  </section>
);

const Step = ({ n, children }) => (
  <li className="flex gap-3">
    <span className="flex-shrink-0 h-6 w-6 bg-[#FF9800] text-[#1A2332] text-xs font-bold flex items-center justify-center font-mono">
      {n}
    </span>
    <span>{children}</span>
  </li>
);

const Pill = ({ children, tone = "zinc" }) => {
  const tones = {
    zinc: "bg-zinc-100 text-zinc-700 border-zinc-300",
    green: "bg-emerald-50 text-emerald-700 border-emerald-300",
    red: "bg-red-50 text-red-700 border-red-300",
  };
  return (
    <span className={`inline-flex items-center px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider border ${tones[tone]}`}>
      {children}
    </span>
  );
};

export default function Help() {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (user) {
    return (
      <Layout>
        <HelpContent />
      </Layout>
    );
  }
  return (
    <MarketingLayout dataTestId="help-page-public">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-10 py-10">
        <HelpContent />
      </div>
    </MarketingLayout>
  );
}

function HelpContent() {
  return (
    <div className="space-y-8" data-testid="help-page">
      {/* Hero */}
      <div className="flex items-start justify-between gap-4 border-b border-zinc-300 pb-6">
        <div>
          <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-500">
            Standard Operating Procedure
          </div>
          <h1 className="font-display text-3xl sm:text-4xl mt-1 text-zinc-900">Quick-Start Guide</h1>
          <p className="text-sm text-zinc-600 mt-2 max-w-2xl">
            A one-page guide to signing in and running daily inventory work in ezItemizer. Covers all three roles:
            Admin, Warehouse Staff, and Viewer.
          </p>
        </div>
        <div className="hidden md:flex items-center gap-3 font-mono text-[10px] uppercase tracking-widest text-zinc-500">
          <span>Updated Feb 2026</span>
          <a
            href="/ezItemizer_SOP.pdf"
            download
            data-testid="help-download-pdf"
            className="inline-flex items-center gap-2 bg-[#1A2332] hover:bg-[#FF9800] hover:text-[#1A2332] text-white px-3 py-2 text-[10px] font-bold tracking-widest transition-colors"
          >
            <DownloadSimple size={14} weight="bold" />
            Download PDF
          </a>
        </div>
      </div>

      {/* Mobile download button — full-width row below the hero */}
      <a
        href="/ezItemizer_SOP.pdf"
        download
        data-testid="help-download-pdf-mobile"
        className="md:hidden flex items-center justify-center gap-2 bg-[#1A2332] hover:bg-[#0F172A] text-white px-4 py-3 text-xs font-bold uppercase tracking-widest transition-colors"
      >
        <DownloadSimple size={16} weight="bold" />
        Download PDF
      </a>

      {/* TOC */}
      <nav className="bg-zinc-50 border border-zinc-300 p-4 grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
        {[
          ["sign-in", "1. Sign in"],
          ["roles", "2. Roles"],
          ["dashboard", "3. Dashboard"],
          ["workflows", "4. Daily workflows"],
          ["admin", "5. Admin tasks"],
          ["plans", "6. Plan features"],
          ["troubleshooting", "7. Troubleshooting"],
          ["help", "8. Get help"],
        ].map(([id, label]) => (
          <a
            key={id}
            href={`#${id}`}
            className="px-2 py-1.5 hover:bg-white hover:border-[#FF9800] border border-transparent transition-colors"
          >
            {label}
          </a>
        ))}
      </nav>

      {/* 1. Sign in */}
      <Section id="sign-in" icon={SignInIcon} title="1. Sign in">
        <ol className="space-y-2.5">
          <Step n={1}>
            Open <span className="font-mono">ezitemizer.com</span>, or tap the ezItemizer icon on your home screen if you've
            installed it as an app (recommended on phones).
          </Step>
          <Step n={2}>Enter your email and password.</Step>
          <Step n={3}>Tap <strong>Sign in</strong>.</Step>
        </ol>
        <div className="bg-zinc-50 border-l-4 border-[#FF9800] p-3 mt-4 text-xs space-y-2">
          <p><strong>Forgot password?</strong> Tap <em>Forgot password?</em> on the sign-in screen. The email link expires in 60 minutes.</p>
          <p><strong>5 wrong attempts</strong> locks the account for 15 minutes.</p>
          <p>
            <strong>Add to home screen:</strong>{" "}
            <em>iPhone (Safari)</em> Share → Add to Home Screen.{" "}
            <em>Android (Chrome)</em> menu → Install app.
          </p>
        </div>
      </Section>

      {/* 2. Roles */}
      <Section id="roles" icon={Users} title="2. Roles & what each can do">
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="bg-zinc-100">
              <tr>
                <th className="text-left px-3 py-2 border-b border-zinc-300">Action</th>
                <th className="text-center px-3 py-2 border-b border-zinc-300">Admin</th>
                <th className="text-center px-3 py-2 border-b border-zinc-300">Staff</th>
                <th className="text-center px-3 py-2 border-b border-zinc-300">Viewer</th>
              </tr>
            </thead>
            <tbody>
              {[
                ["View parts, categories, transactions", true, true, true],
                ["Add a new part", true, true, true],
                ["Edit part details (name, SKU, location, threshold)", true, true, true],
                ["See / edit unit cost & inventory value", true, true, false],
                ["Log stock IN / OUT / adjustment", true, true, false],
                ["Camera barcode + QR scanning", true, true, false],
                ["Delete / restore parts", true, false, false],
                ["Add or remove users", true, false, false],
                ["Manage billing / subscription", true, false, false],
                ["Export CSV / PDF reports", true, true, true],
              ].map(([label, a, s, v]) => (
                <tr key={label} className="border-b border-zinc-200">
                  <td className="px-3 py-2">{label}</td>
                  <td className="text-center px-3 py-2">{a ? "✓" : "—"}</td>
                  <td className="text-center px-3 py-2">{s ? "✓" : "—"}</td>
                  <td className="text-center px-3 py-2">{v ? "✓" : "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="text-xs text-zinc-500 mt-2 italic">
          Camera scanning is on the Team and Pro plans only — Basic plan has it disabled.
        </p>
      </Section>

      {/* 3. Dashboard */}
      <Section id="dashboard" icon={Lightning} title="3. The Dashboard">
        <p>When you sign in you land on the Operations Dashboard. At a glance:</p>
        <ul className="list-disc list-inside space-y-1.5 ml-1">
          <li><strong>Total Parts</strong> — distinct SKUs in your active inventory.</li>
          <li><strong>Inventory Value</strong> — units × unit cost across all parts <em>(hidden for Viewer)</em>.</li>
          <li><strong>Low Stock</strong> — count of parts at or below their threshold.</li>
          <li><strong>Categories</strong> — active part categories.</li>
          <li><strong>Low-stock alerts</strong> — table of parts that need reordering.</li>
          <li><strong>By category</strong> — value-by-category breakdown.</li>
          <li><strong>Send feedback</strong> — top-right of the hero, opens an in-app form.</li>
        </ul>
      </Section>

      {/* 4. Workflows */}
      <Section id="workflows" icon={Package} title="4. Daily workflows">
        <h3 className="font-bold text-zinc-900 mt-2">A. Add a new part <Pill tone="green">Any role</Pill></h3>
        <ol className="space-y-2 ml-1">
          <Step n={1}>Go to <strong>Parts</strong> in the sidebar.</Step>
          <Step n={2}>Tap the orange <strong>Add Part</strong> button (top-right).</Step>
          <Step n={3}>Fill in SKU (must be unique), Part #, Name, Category, Initial stock, Low-stock threshold. Optional: Unit cost (Admin/Staff), Location, Supplier.</Step>
          <Step n={4}>Tap <strong>Save</strong>.</Step>
        </ol>

        <h3 className="font-bold text-zinc-900 mt-5">B. Log stock movement <Pill>Admin / Staff</Pill></h3>
        <ol className="space-y-2 ml-1">
          <Step n={1}>On the Parts page, tap the <strong>In/Out adjust</strong> icon on a row.</Step>
          <Step n={2}>Pick <strong>IN</strong> (received), <strong>OUT</strong> (used/shipped), or <strong>Adjustment</strong> (set to count after a physical recount).</Step>
          <Step n={3}>Enter quantity, optional unit cost (IN only), optional note. Save.</Step>
        </ol>

        <h3 className="font-bold text-zinc-900 mt-5">C. Scan a barcode <Pill>Admin / Staff · Team or Pro plan</Pill></h3>
        <ol className="space-y-2 ml-1">
          <Step n={1}>On the Parts page, tap the <strong>camera icon</strong> in the search bar.</Step>
          <Step n={2}>Allow camera access on first use.</Step>
          <Step n={3}>Aim at the barcode or QR code — the matching part is filtered automatically.</Step>
        </ol>

        <h3 className="font-bold text-zinc-900 mt-5">D. Find a part fast <Pill tone="green">Any role</Pill></h3>
        <p>Type into the search bar — it searches SKU, Part #, name, location, supplier. Filter by Category or "Low stock only" in the toolbar.</p>

        <h3 className="font-bold text-zinc-900 mt-5">E. Pull a report <Pill tone="green">Any role</Pill></h3>
        <p>Sidebar → <strong>Reports</strong>. Inventory or Movements as CSV/PDF for any date range. Financial reports (P/L style by category) require the Pro plan.</p>

        <h3 className="font-bold text-zinc-900 mt-5">F. Sign out</h3>
        <p>Top-right of the header, right next to your email — tap <strong>Sign out</strong>.</p>
      </Section>

      {/* 5. Admin */}
      <Section id="admin" icon={Buildings} title="5. Admin tasks">
        <h3 className="font-bold text-zinc-900 mt-1">Add or remove a user</h3>
        <p>Sidebar → <strong>Users</strong> → <strong>Add user</strong>. Enter name, email, role, and a starting password. The user gets a welcome email automatically. Remove with the trash icon on the row.</p>

        <h3 className="font-bold text-zinc-900 mt-4">Manage categories</h3>
        <p>Sidebar → <strong>Categories</strong> → Add / Edit / Delete. A category with active parts can't be deleted until those parts are reassigned.</p>

        <h3 className="font-bold text-zinc-900 mt-4">Restore a deleted part</h3>
        <p>Parts page → toggle <strong>Show deleted</strong>. Tap the <strong>Restore</strong> icon on the row. <em>Restore fails if another active part has since taken the same SKU.</em></p>

        <h3 className="font-bold text-zinc-900 mt-4">Billing</h3>
        <p>Sidebar → <strong>Billing</strong>. Upgrade or change plan, or open the Stripe Customer Portal for invoices and payment methods.</p>
      </Section>

      {/* 6. Plans */}
      <Section id="plans" icon={CreditCard} title="6. Plan-aware features">
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="bg-zinc-100">
              <tr>
                <th className="text-left px-3 py-2 border-b border-zinc-300">Feature</th>
                <th className="text-center px-3 py-2 border-b border-zinc-300">Basic</th>
                <th className="text-center px-3 py-2 border-b border-zinc-300">Team</th>
                <th className="text-center px-3 py-2 border-b border-zinc-300">Pro</th>
              </tr>
            </thead>
            <tbody>
              {[
                ["Unlimited parts, audit trail, alerts", "✓", "✓", "✓"],
                ["Users included", "2", "5", "10"],
                ["Warehouse Staff role", "—", "✓", "✓"],
                ["Camera barcode / QR scanning", "—", "✓", "✓"],
                ["Daily digest emails", "—", "✓", "✓"],
                ["Financial reports", "—", "—", "✓"],
              ].map(([label, b, t, p]) => (
                <tr key={label} className="border-b border-zinc-200">
                  <td className="px-3 py-2">{label}</td>
                  <td className="text-center px-3 py-2">{b}</td>
                  <td className="text-center px-3 py-2">{t}</td>
                  <td className="text-center px-3 py-2">{p}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Section>

      {/* 7. Troubleshooting */}
      <Section id="troubleshooting" icon={Wrench} title="7. Troubleshooting">
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="bg-zinc-100">
              <tr>
                <th className="text-left px-3 py-2 border-b border-zinc-300">Issue</th>
                <th className="text-left px-3 py-2 border-b border-zinc-300">Try this</th>
              </tr>
            </thead>
            <tbody>
              {[
                ["Locked out of your account", "Wait 15 minutes or use Forgot password."],
                ["Reset email never arrived", "Check spam; the link expires in 60 minutes."],
                ['"SKU already exists" when adding', "That SKU is in use. Pick a different one or restore the existing part."],
                ["Camera button not showing", "Either your role isn't Admin/Staff, or your plan is Basic — upgrade to Team to unlock scanning."],
                ["Cost columns are missing", "You're signed in as a Viewer — cost is hidden by design."],
                ["Low-stock email not arriving", "Check spam; verify Notifications preferences (Admin)."],
              ].map(([q, a]) => (
                <tr key={q} className="border-b border-zinc-200 align-top">
                  <td className="px-3 py-2 font-semibold text-zinc-900">{q}</td>
                  <td className="px-3 py-2">{a}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Section>

      {/* 8. Get help */}
      <Section id="help" icon={Lifebuoy} title="8. Getting help">
        <ul className="list-disc list-inside space-y-1.5 ml-1">
          <li><strong>In-app:</strong> Send feedback button on the Dashboard hero.</li>
          <li><strong>Email:</strong> <a className="text-[#1A2332] underline" href="mailto:support@ezitemizer.com">support@ezitemizer.com</a> — we reply within 1 business day.</li>
          <li><strong>FAQ:</strong> <Link to="/faq" className="text-[#1A2332] underline">ezitemizer.com/faq</Link></li>
        </ul>
      </Section>
    </div>
  );
}
